import { useParams, Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatINR } from '@new-wealth/shared';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { useConfirm } from '../components/Confirm.js';
import { useState } from 'react';

const rowPill: Record<string, string> = {
  Paid: 'text-success', Scheduled: 'text-text-muted', Skipped: 'text-warn', Failed: 'text-danger',
};

/**
 * Which bank account THIS NCD's interest goes to.
 *
 * Only worth showing when the customer has more than one account on file —
 * with a single account there is nothing to choose. Unpinned, the NCD follows
 * whichever account is the customer's active one, so the empty option says so
 * rather than reading like a blank.
 */
function PayoutAccount({ appId, customerId, current, onChange }: { appId: number; customerId: number; current: number | null; onChange: () => void }) {
  const { confirm } = useConfirm();
  const { data } = useQuery({ queryKey: ['cust-banks', customerId], queryFn: () => api.get<any>(`/api/customers/${customerId}`) });
  const set = useMutation({ mutationFn: (bankId: number | null) => api.post(`/api/applications/${appId}/payout-account`, { bank_account_id: bankId }), onSuccess: onChange });
  const accounts = data?.bankAccounts ?? [];
  if (accounts.length < 2) return null;

  // The account number alone can't be told apart when a customer holds several
  // accounts at the same bank (owner 2026-07-28) — two Canara accounts differ
  // by one digit. Show who the money is payable to and the IFSC as well, since
  // those are exactly what ops check against the NEFT sheet.
  const label = (b: any) =>
    [b.account_number, b.holder_name, b.ifsc, b.bank_name].filter(Boolean).join(' · ')
    + (b.penny_drop_status === 'Verified' ? '' : ` (${b.penny_drop_status})`);
  const active = accounts.find((b: any) => b.is_active);

  return (
    <select
      className="text-xs border border-border-strong rounded px-2 py-1 max-w-full"
      value={current ?? ''}
      onChange={async (e) => {
        const id = e.target.value ? Number(e.target.value) : null;
        const chosen = accounts.find((b: any) => Number(b.id) === id);
        if (chosen && chosen.penny_drop_status !== 'Verified'
            && !await confirm({
              title: 'This account is not penny-drop verified',
              body: `${label(chosen)} is ${chosen.penny_drop_status}. Send this NCD's interest there anyway?`,
              confirmLabel: 'Use it anyway', danger: true,
            })) return;
        set.mutate(id);
      }}
    >
      <option value="">Customer default{active ? ` — ${label(active)}` : ''}</option>
      {accounts.map((b: any) => <option key={b.id} value={b.id}>{label(b)}</option>)}
    </select>
  );
}

/**
 * Download the interest & redemption schedule as a spreadsheet (owner
 * 2026-10-06). The server builds it from the same call that fills the table
 * below, so the file is that table — plus the term totals and the date each
 * settled row was paid, neither of which fits on screen.
 *
 * It fetches rather than linking, for the reasons SignedAgreementActions gives:
 * an expired 15-minute session is refreshed (api.blob) instead of handing the
 * browser a page of raw JSON, and a failure lands HERE as a readable sentence.
 */
function ScheduleDownload({ appId, fileStem }: { appId: number; fileStem: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function download() {
    setErr(''); setBusy(true);
    try {
      const blob = await api.blob(`/api/applications/${appId}/schedule.xlsx`);
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href; a.download = `schedule-${fileStem}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not download the schedule.');
    } finally { setBusy(false); }
  }

  // normal-case / font-normal: the card header this sits in is uppercase+semibold,
  // which a button would otherwise inherit.
  return (
    <div className="flex items-center gap-2">
      {err && <span role="alert" className="text-xs text-danger normal-case font-normal">{err}</span>}
      <button type="button" onClick={download} disabled={busy} title="Download this schedule as an Excel file"
        className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg normal-case font-normal disabled:opacity-50">
        {busy ? 'Preparing…' : '↓ Excel'}
      </button>
    </div>
  );
}

/** Lifecycle actions for an Active investment: premature/maturity redemption,
 * rollover, holder transfer, transformation. Each posts and lands in approvals. */
// Local (not UTC) YYYY-MM-DD — the redemption date defaults to today so staff
// don't have to type it, and toISOString would roll to the next day late in an
// IST evening.
const todayLocalISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function LifecycleActions({ appId, locker, onDone, onError }: { appId: number; locker?: { redeemable: number; linked_to_lockers: number } | null; onDone: (msg: string) => void; onError: (e: unknown) => void }) {
  const { confirm } = useConfirm();
  const [open, setOpen] = useState<'premature' | 'transfer' | 'transformation' | null>(null);
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  // Defaults to today (editable). Blank is still accepted by the API (it also
  // defaults to today), but showing the date makes the default explicit.
  const [date, setDate] = useState(todayLocalISO());
  const [custQ, setCustQ] = useState('');
  const [toCustomer, setToCustomer] = useState<{ id: number; name: string } | null>(null);
  const [nominee, setNominee] = useState({ nominee_name: '', nominee_bank_name: '', nominee_account: '', nominee_ifsc: '' });

  const search = useQuery({
    queryKey: ['cust-search', custQ],
    queryFn: () => api.get<{ customers: { id: number; full_name: string; customer_code: string }[] }>(`/api/dashboard/search?q=${encodeURIComponent(custQ)}`),
    enabled: open === 'transfer' && custQ.trim().length >= 2 && !toCustomer,
  });

  const fire = (p: Promise<unknown>, msg: string) =>
    p.then(() => { setOpen(null); setReason(''); setAmount(''); setDate(todayLocalISO()); setToCustomer(null); setCustQ(''); onDone(msg); }).catch(onError);

  const inp = 'px-2.5 py-1.5 text-xs border border-border-strong rounded outline-none focus:border-primary';
  const act = 'text-xs border border-border rounded px-3 py-1.5 hover:bg-bg';
  const go = 'text-xs bg-primary text-white rounded px-3 py-1.5 disabled:opacity-40 hover:bg-primary-hover';

  return (
    <div className="bg-surface border border-border rounded-lg shadow-card mt-4 p-4">
      <div className="text-xs font-semibold text-text-label uppercase tracking-wide mb-2.5">Lifecycle actions</div>
      <div className="flex gap-2 flex-wrap">
        <button className={act} onClick={() => setOpen(open === 'premature' ? null : 'premature')}>Redeem early…</button>
        <button className={act} onClick={async () => { if (await confirm({ title: 'Initiate maturity redemption?', body: 'It goes to a checker before anything is paid.', confirmLabel: 'Initiate' })) fire(api.post('/api/redemptions/maturity', { application_id: appId }), 'Maturity redemption initiated — awaiting approval.'); }}>Redeem at maturity</button>
        <button className={act} onClick={async () => { if (await confirm({ title: 'Roll this investment over into a fresh one?', body: 'It goes to a checker before anything changes.', confirmLabel: 'Roll over' })) fire(api.post('/api/ncd-events/rollover', { application_id: appId }), 'Rollover initiated — awaiting approval.'); }}>Rollover</button>
        <button className={act} onClick={() => setOpen(open === 'transfer' ? null : 'transfer')}>Transfer holder…</button>
        <button className={act} onClick={() => setOpen(open === 'transformation' ? null : 'transformation')}>Transformation (deceased)…</button>
      </div>

      {open === 'premature' && (
        <div className="mt-3">
          <div className="flex gap-2 flex-wrap items-center">
            <input className={`${inp} w-64`} placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
            <input className={`${inp} w-44`} type="number" min="1" placeholder="Amount (blank = all)" value={amount} onChange={(e) => setAmount(e.target.value)}
              title="Partial withdrawal — leave blank to redeem everything redeemable" />
            <input className={inp} type="date" value={date} onChange={(e) => setDate(e.target.value)} title="Redemption date (optional, default today)" />
            <button className={go} disabled={reason.trim().length < 2 || (amount !== '' && !(Number(amount) > 0))}
              onClick={() => fire(api.post('/api/redemptions/premature', {
                application_id: appId, reason,
                ...(amount ? { amount: Number(amount) } : {}),
                ...(date ? { redemption_date: date } : {}),
              }), 'Premature redemption initiated — awaiting approval.')}>Initiate</button>
          </div>
          {/* A pledged investment can only redeem its free portion — the rest secures a locker. */}
          {locker && locker.linked_to_lockers > 0 && (
            <div className="text-xs text-text-muted mt-2">
              {formatINR(locker.linked_to_lockers)} is pledged to a live locker deposit and cannot be redeemed.
              Redeemable now: <b>{formatINR(locker.redeemable)}</b>. Leave the amount blank to redeem all of it and keep the locker live.
            </div>
          )}
        </div>
      )}

      {open === 'transfer' && (
        <div className="flex gap-2 flex-wrap items-center mt-3">
          {toCustomer ? (
            <span className="text-xs bg-bg rounded px-2 py-1.5">→ {toCustomer.name} <button className="text-text-muted hover:text-danger ml-1" onClick={() => setToCustomer(null)}>×</button></span>
          ) : (
            <span className="relative">
              <input className={`${inp} w-64`} placeholder="Search new holder (name/PAN/phone)…" value={custQ} onChange={(e) => setCustQ(e.target.value)} autoFocus />
              {(search.data?.customers ?? []).length > 0 && (
                <span className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded shadow-card z-10 block max-h-48 overflow-auto">
                  {search.data!.customers.map((c) => (
                    <button key={c.id} className="w-full text-left px-3 py-1.5 text-xs hover:bg-bg block" onClick={() => setToCustomer({ id: c.id, name: c.full_name })}>
                      {c.full_name} <span className="font-mono text-text-muted">{c.customer_code}</span>
                    </button>
                  ))}
                </span>
              )}
            </span>
          )}
          <input className={`${inp} w-56`} placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <button className={go} disabled={!toCustomer || reason.trim().length < 2}
            onClick={() => fire(api.post('/api/ncd-events/transfer', { application_id: appId, to_customer_id: toCustomer!.id, reason }), 'Holder transfer initiated — awaiting approval.')}>Initiate</button>
        </div>
      )}

      {open === 'transformation' && (
        <div className="flex gap-2 flex-wrap items-center mt-3">
          <input className={`${inp} w-48`} placeholder="Nominee name (required)" value={nominee.nominee_name} onChange={(e) => setNominee({ ...nominee, nominee_name: e.target.value })} autoFocus />
          <input className={`${inp} w-36`} placeholder="Nominee bank" value={nominee.nominee_bank_name} onChange={(e) => setNominee({ ...nominee, nominee_bank_name: e.target.value })} />
          <input className={`${inp} w-40`} placeholder="Account no." value={nominee.nominee_account} onChange={(e) => setNominee({ ...nominee, nominee_account: e.target.value })} />
          <input className={`${inp} w-28`} placeholder="IFSC" value={nominee.nominee_ifsc} onChange={(e) => setNominee({ ...nominee, nominee_ifsc: e.target.value })} />
          <button className={go} disabled={!nominee.nominee_name.trim()}
            onClick={() => fire(api.post('/api/ncd-events/transformation', {
              application_id: appId,
              nominee_name: nominee.nominee_name,
              ...(nominee.nominee_bank_name ? { nominee_bank_name: nominee.nominee_bank_name } : {}),
              ...(nominee.nominee_account ? { nominee_account: nominee.nominee_account } : {}),
              ...(nominee.nominee_ifsc ? { nominee_ifsc: nominee.nominee_ifsc } : {}),
            }), 'Transformation initiated — awaiting approval.')}>Initiate</button>
        </div>
      )}
    </div>
  );
}

export function ApplicationDetailPage() {
  const { confirm, promptText } = useConfirm();
  const { id } = useParams();
  // Where the user came from, carried in router state. Opened from a customer
  // profile → back returns THERE; opened from the Applications list (or a hard
  // refresh, which drops the state) → back goes to the list.
  const back = (useLocation().state as { from?: { path: string; label: string } } | null)?.from
    ?? { path: '/app/applications', label: 'Applications' };
  const qc = useQueryClient();
  const { can, user } = useAuth();
  const [msg, setMsg] = useState('');
  const [note, setNote] = useState('');
  const key = ['application', id];
  // While a signature is out with the customer, re-check every 15s so the page
  // flips to eSigned on its own — no webhook, no manual "Mark eSigned".
  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => api.get<any>(`/api/applications/${id}`),
    refetchInterval: (q) => (q.state.data?.esign_pending ? 15_000 : false),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const run = (p: Promise<unknown>) => p.then(() => { setMsg(''); invalidate(); }).catch((e) => setMsg(e instanceof ApiError ? e.message : 'Failed'));

  /**
   * Send this investment's form for eSign — first attempt or any attempt after.
   *
   * A customer who abandons the Aadhaar OTP leaves the Digio document open, so
   * the session stays 'requested' and the old screen never offered the button
   * again: one failed attempt and the investment could not be signed at all,
   * by eSign OR on paper (owner 2026-09-17). Sending again is allowed until a
   * signature exists.
   *
   * A second send is confirmed first, because it costs the customer another SMS
   * and the earlier link stays live — whichever one they sign is recorded.
   */
  const sendForEsign = async () => {
    const alreadyOut = data?.esign?.state === 'awaiting' || data?.esign?.state === 'stalled';
    if (alreadyOut) {
      const waited = data?.esign?.days_waiting === 0 ? 'today' : `${data?.esign?.days_waiting} day(s) ago`;
      const ok = await confirm({
        title: 'Send another signing link?',
        body: `A link was sent ${waited} and has not been signed yet. The customer gets a fresh link by SMS. `
          + 'This does NOT cancel the earlier one — whichever link they sign is the one recorded.',
        confirmLabel: 'Send a new link',
      });
      if (!ok) return;
    }
    await run(api.post<{ sign_url: string | null; stub: boolean }>(`/api/applications/${id}/esign/initiate`).then((r) => {
      if (r.sign_url && !r.stub) window.open(r.sign_url, '_blank', 'noopener');
      else setNote(r.stub ? 'eSign is in sandbox mode (no Digio credentials) — nothing was sent.' : 'Signing session created.');
    }));
  };

  /**
   * The signed application form, scanned back in (owner 2026-09-03: "currently
   * i only see esigning no manual signature upload provisions").
   *
   * The form is already pre-filled and already carries signature boxes — it is
   * the document Digio signs — so staff print that same form, get it signed and
   * upload the result. Asks for the date ON THE PAPER, because the scan often
   * comes back days after the customer signed.
   */
  const uploadSignedApp = async (file: File) => {
    const signedOn = await promptText({
      title: 'When did the customer sign?',
      body: 'The date written on the form, not today.',
      label: 'Date signed', inputType: 'date', confirmLabel: 'Upload signed form',
    });
    if (!signedOn) return;
    const data_base64 = await new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onerror = () => reject(new Error('unreadable'));
      fr.onload = () => resolve(String(fr.result).split(',')[1] ?? '');
      fr.readAsDataURL(file);
    }).catch(() => null);
    if (!data_base64) { setMsg('Could not read that file.'); return; }
    await run(api.post(`/api/applications/${id}/signed-upload`, {
      data_base64, filename: file.name, signed_on: signedOn,
    }).then(() => setNote('Signed application form uploaded.')));
  };

  if (isLoading) return <div className="text-text-muted">Loading…</div>;
  if (error) return <div className="text-danger">Application not found or out of scope.</div>;
  const a = data.application;

  return (
    <div className="w-full">
      <Link to={back.path} className="text-xs text-text-muted hover:text-primary">← {back.label}</Link>
      <div className="flex items-center gap-3 mt-1">
        <h1 className="text-xl font-bold tracking-tight m-0 font-mono">{a.application_no}</h1>
        <span className="text-xs rounded px-1.5 py-0.5 bg-bg">{a.status}</span>
      </div>
      <p className="text-sm text-text-muted mt-1">{a.customer_name} · {a.series_code} · {formatINR(a.total_amount)}</p>
      <InvestmentDate a={a} appId={Number(id)}
        canEdit={['ncd_manager', 'admin', 'super_admin'].includes(user?.role ?? '') && (data.lines?.length ?? 0) === 1}
        onErr={setMsg} />
      <CreditedTo app={a} appId={Number(id)} canEdit={can('applications:update')} onDone={invalidate} onErr={setMsg} />
      {msg && <div className="text-xs text-danger mt-2">{msg}</div>}
      {note && <div className="text-xs text-primary mt-2">{note}</div>}

      <div className="flex gap-2 mt-3 items-center flex-wrap">
        {a.status === 'PendingApproval' && (
          <span className="text-xs text-text-muted">Awaiting investment approval — approve in Approvals to take it live.</span>
        )}
        {/* eSign, or the same form signed by hand. There is deliberately NO
            "mark this signed" control: a signature is recorded only when Digio
            says the customer signed, or when an actual signed document is
            uploaded — never because a member of staff said so (owner
            2026-08-29). What IS always offered, until a signature exists, is
            another attempt: eSign again, print and sign on paper, or upload a
            signed form. An abandoned Digio session stays open on their side, so
            gating these on "nothing is out" left a failed attempt with no way
            forward at all (owner 2026-09-17). */}
        {can('applications:mark-esigned') && data.esign?.can_send && ['PendingActivation', 'PendingEsign', 'Active'].includes(a.status) && (
          <>
            <button onClick={() => void sendForEsign()}
              title={data.esign.state === 'not_sent'
                ? 'Sends the pre-filled form to the customer for Aadhaar eSign.'
                : 'The last link was not signed. This sends a fresh one; the old link stays valid too.'}
              className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg">
              {data.esign.state === 'not_sent' ? 'Send for eSign' : 'Send eSign again'}
            </button>
            {/* The form is already pre-filled and already carries signature
                boxes — it is the document Digio signs. Signing on paper prints
                that same form. */}
            <a href={`/api/reports/application-form/${id}.pdf`} target="_blank" rel="noreferrer"
               className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg">↓ Print form to sign</a>
            <label className="text-xs bg-primary text-white rounded px-3 py-1.5 hover:bg-primary-hover cursor-pointer">
              Upload signed form
              <input type="file" className="hidden" accept="application/pdf,image/jpeg,image/png"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void uploadSignedApp(f); }} />
            </label>
          </>
        )}
        {data.esign?.state === 'awaiting' && (
          <span className="text-xs text-warn bg-[color:var(--warn-bg)] rounded px-2 py-1"
            title={`Sent ${data.esign.days_waiting === 0 ? 'today' : `${data.esign.days_waiting} day(s) ago`}. Digio is asked every 15 seconds; this becomes eSigned on its own once the customer signs.`}>
            ⏳ Sent to customer — waiting for them to sign
            {(data.esign.attempts ?? 0) > 1 && ` · attempt ${data.esign.attempts}`}
          </span>
        )}
        {data.esign?.state === 'stalled' && (
          <>
            <span className="text-xs text-danger bg-[color:var(--danger-bg)] rounded px-2 py-1"
              title={`Automatic checking stops after ${data.esign.poll_window_days} days, so this is no longer being chased.`}>
              ⚠ Unsigned for {data.esign.days_waiting} days — no longer checked automatically
            </span>
            {can('applications:mark-esigned') && (
              <button
                title="Asks Digio once whether this has been signed. It can only confirm a real signature — it cannot mark an unsigned document as signed."
                onClick={() => run(api.post<{ ok: boolean; signed?: boolean; reason?: string }>(`/api/applications/${id}/esign/check`).then((r) => {
                  if (!r.ok) setNote(r.reason === 'no-session' ? 'Nothing is out for signature on this investment.' : 'eSign is not configured on this server.');
                  else setNote(r.signed ? 'Digio confirms it is signed — recorded.' : 'Digio says it is still unsigned. The customer has not signed yet.');
                }))}
                className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg">Check with Digio</button>
            )}
          </>
        )}
        {/* Never a bare "eSigned". Before this the label said eSigned for every
            signed form, including ones signed on paper — a claim we could not
            evidence. A legacy row with no method recorded says exactly that. */}
        {a.esigned_at && (
          <span className="text-xs text-success">
            {a.signing_method === 'physical' ? 'Physically signed' : a.signing_method === 'esign' ? 'eSigned' : 'Signed · method not recorded'} ✓
            {a.signed_on ? ` · ${String(a.signed_on).slice(0, 10)}` : ''}
          </span>
        )}
        {a.esigned_pdf_path && (
          <a href={`/api/reports/esigned/${id}.pdf`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">eSigned application</a>
        )}
        {a.signed_doc_filename && (
          <a href={`/api/applications/${id}/signed-application.pdf`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Signed application form</a>
        )}
        {can('applications:update') && (
          <label className="text-xs flex items-center gap-1.5 border border-border rounded px-3 py-1.5" title="Money came from a locker (LockerHub-originated deposits flag themselves automatically)">
            <input type="checkbox" checked={!!a.is_locker_deposit}
              onChange={(e) => run(api.post(`/api/applications/${id}/locker-deposit`, { is_locker_deposit: e.target.checked }))} />
            Locker deposit
          </label>
        )}
        {a.receipt_file_path && <a href={`/api/applications/${id}/receipt`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">View receipt</a>}
        <a href={`/api/reports/application-form/${id}.pdf`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Application form</a>
        {a.status === 'Active' && <a href={`/api/reports/acknowledgment/${id}.pdf`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Acknowledgement</a>}
        {/* Mirrors the ROUTE's permissions, which this used to disagree with:
            the API accepted notifications:admin too, but the button never
            appeared for it. Same list, one place to change. */}
        {a.status === 'Active'
          && (can('applications:update') || can('notifications:admin') || can('notifications:send-customer')) && (
          <button
            onClick={() => run(api.post<{ ok: boolean; status: string; error: string | null; phone: string }>(`/api/applications/${id}/whatsapp-ack`)
              .then((r) => setNote(r.ok ? `Acknowledgement sent on WhatsApp to ${r.phone}.` : `WhatsApp send ${r.status}${r.error ? ' — ' + r.error : ''}.`)))}
            className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg" title="Send the acknowledgement PDF to the customer over WhatsApp (ncd_akn)">📲 Ack on WhatsApp</button>
        )}
        {/* No per-investment bond here (owner 2026-08-12). A customer holding
            four investments in a series gets ONE certificate covering all of
            them — the consolidated bond on their profile — so offering a
            separate one per investment here produced a second, contradictory
            document for the same money. The consolidated bond is now the only
            bond staff issue: CustomerDetail → the series group. */}
        {a.allotment_date && (
          <a href={`/api/reports/allotment/${id}.pdf`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Allotment letter</a>
        )}
        {/* Has the customer actually RECEIVED their bond? (owner 2026-08-19).
            Every other bond field says what we produced — generated, eSigned,
            filed — none of them says it reached the customer's hands. */}
        <BondHandover app={a} appId={Number(id)} onDone={invalidate} onErr={setMsg} />
        {can('applications:update') && (
          <label className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg cursor-pointer">
            {a.receipt_file_path ? 'Replace receipt…' : 'Upload receipt…'}
            <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              if (file.size > 4 * 1024 * 1024) { setNote(''); setMsg('Receipt must be under 4 MB.'); return; }
              const reader = new FileReader();
              reader.onload = () => {
                const data_base64 = String(reader.result).split(',')[1] ?? '';
                run(api.post(`/api/applications/${id}/receipt`, { filename: file.name, mime: file.type || 'application/octet-stream', data_base64 }));
              };
              reader.readAsDataURL(file);
            }} />
          </label>
        )}
        {can('applications:update') && a.status === 'Active' && <PayoutAccount appId={Number(id)} customerId={a.customer_id} current={a.payout_bank_account_id ? Number(a.payout_bank_account_id) : null} onChange={invalidate} />}
      </div>

      {/* Locker pledge — ONE investment, part of it backing locker deposits. */}
      {data.locker && data.locker.linked_to_lockers > 0 && (
        <div className="bg-surface border border-border rounded-lg shadow-card p-5 mt-4">
          <h2 className="text-xs font-semibold text-text-label uppercase tracking-wide mb-3">Locker deposit pledge</h2>
          <div className="flex flex-wrap gap-6 text-sm mb-2">
            <div><div className="text-text-muted text-xs">Total investment</div><div className="font-semibold">{formatINR(data.locker.outstanding)}</div></div>
            <div><div className="text-text-muted text-xs">Linked to lockers</div><div className="font-semibold text-warn">{formatINR(data.locker.linked_to_lockers)}</div></div>
            <div><div className="text-text-muted text-xs">Free NCD</div><div className="font-semibold">{formatINR(data.locker.free_ncd)}</div></div>
            <div><div className="text-text-muted text-xs">Redeemable</div><div className="font-semibold text-success">{formatINR(data.locker.redeemable)}</div></div>
          </div>
          <p className="text-xs text-text-muted mb-3">This is a single investment — the linked amount is the locker's security and can't be redeemed until the link is released.</p>
          <table className="w-full text-sm border-collapse">
            <thead><tr className="border-b border-border">
              {['Locker', 'Size', 'Pledged', 'Status', ''].map((h) => (
                <th key={h} className="py-2 px-3 text-xs font-semibold text-text-label uppercase tracking-wide text-left">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {data.locker.links.map((l: any) => (
                <tr key={l.id} className={`border-b border-border last:border-0 ${l.status !== 'active' ? 'opacity-50' : ''}`}>
                  <td className="py-2 px-3 font-mono text-xs">{l.locker_no ?? l.lockerhub_application_id}</td>
                  <td className="py-2 px-3">{l.locker_size ?? '—'}</td>
                  <td className="py-2 px-3 font-medium">{formatINR(l.linked_amount)}</td>
                  <td className="py-2 px-3"><span className={`text-[11px] rounded px-1.5 py-0.5 ${l.status === 'active' ? 'bg-[color:var(--warn-bg)] text-warn' : 'bg-bg text-text-muted'}`}>{l.status}</span></td>
                  <td className="py-2 px-3 text-right">
                    {l.status === 'active' && can('lockers:enroll') && (
                      <button className="text-xs text-danger hover:underline"
                        onClick={async () => {
                          const r = await promptText({
                            title: 'Release this locker link?',
                            body: 'The pledged amount becomes redeemable again.',
                            label: 'Reason (e.g. locker closed)', confirmLabel: 'Release', danger: true,
                          });
                          if (r) run(api.post(`/api/lockers/deposit-links/${l.id}/release`, { reason: r }));
                        }}>Release</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {can('redemptions:initiate') && a.status === 'Active' && (
        <LifecycleActions appId={Number(id)} locker={data.locker}
          onDone={(m) => { setMsg(''); setNote(m); invalidate(); }}
          onError={(e) => { setNote(''); setMsg(e instanceof ApiError ? e.message : 'Failed'); }} />
      )}

      {/* How the money actually arrived. Shown whenever there is more than one
          credit: the investment is ONE ₹1,00,000 NCD, but it may have been paid
          ₹50,000 + ₹50,000 on different days against different references, and
          a year from now nobody will remember that from the total alone.
          A single-credit investment shows nothing here — the header already
          says everything there is to say. */}
      {data.lines?.length > 1 && (
        <div className="bg-surface border border-border rounded-lg shadow-card mt-4 overflow-hidden">
          <div className="px-4 py-3 border-b border-border text-xs font-semibold text-text-label uppercase tracking-wide">
            Payment breakup — {data.lines.length} credits
          </div>
          <table className="w-full text-sm border-collapse">
            <thead><tr className="border-b border-border">
              {['#', 'Received', 'Method', 'Reference', 'Amount', ''].map((h, i) => (
                <th key={h + i} className={`py-2 px-3 text-xs font-semibold text-text-label uppercase tracking-wide ${h === 'Amount' ? 'text-right' : 'text-left'}`}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {data.lines.map((l: any, i: number) => (
                <tr key={l.id} className="border-b border-border last:border-0">
                  <td className="py-2 px-3 text-text-muted">{i + 1}</td>
                  {/* '—' where a value was never recorded. The parts clubbed
                      before this was stored have no detail of their own, and
                      inventing one would be worse than admitting the gap. */}
                  {/* Each credit earns from ITS OWN date, so a mistyped one is
                      real money — and the investment-date editor above cannot
                      touch it (it refuses a clubbed investment, since there is
                      no single date to move). Super Admin corrects it here. */}
                  <td className="py-2 px-3 whitespace-nowrap">
                    <CreditDate line={l} appId={Number(id)}
                      canEdit={['ncd_manager', 'admin', 'super_admin'].includes(user?.role ?? '')}
                      onErr={setMsg} />
                  </td>
                  <td className="py-2 px-3">{l.collection_method ?? '—'}</td>
                  <td className="py-2 px-3 font-mono text-xs">{l.collection_reference ?? '—'}</td>
                  <td className="py-2 px-3 text-right mono font-medium">{formatINR(l.amount)}</td>
                  <td className="py-2 px-3 text-right">
                    {l.receipt_file_path && (
                      <a href={`/api/applications/${id}/lines/${l.id}/receipt`} target="_blank" rel="noreferrer"
                         className="text-xs text-primary hover:underline">receipt</a>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-border-strong font-semibold">
                <td className="py-2 px-3" colSpan={4}>Total invested</td>
                <td className="py-2 px-3 text-right mono">{formatINR(a.total_amount)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-surface border border-border rounded-lg shadow-card mt-4 overflow-hidden">
        <div className="px-4 py-3 border-b border-border text-xs font-semibold text-text-label uppercase tracking-wide flex items-center justify-between gap-3">
          <span>Interest &amp; redemption schedule</span>
          {data.schedule.length > 0 && <ScheduleDownload appId={Number(id)} fileStem={a.application_no} />}
        </div>
        {data.schedule.length === 0 ? (
          <div className="p-6 text-center text-text-muted text-sm">Schedule is generated at allotment.</div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs font-semibold text-text-label border-b border-border">
              <th className="px-4 py-2">Due date</th><th className="px-4 py-2">Type</th>
              <th className="px-4 py-2 text-right">Gross</th><th className="px-4 py-2 text-right">TDS</th>
              <th className="px-4 py-2 text-right">Net</th><th className="px-4 py-2">Status</th></tr></thead>
            <tbody className="divide-y divide-border">
              {data.schedule.map((r: any) => (
                <tr key={r.id}>
                  <td className="px-4 py-1.5 mono">{r.due_date}</td>
                  <td className="px-4 py-1.5">
                    {r.due_type}
                    {/* From the batch after the broken period, a clubbed
                        investment's tranches pay as ONE combined line. */}
                    {r.combined && (
                      <span className="ml-1.5 text-xs text-text-muted" title={`Combined interest across ${r.tranche_count} tranches`}>
                        · {r.tranche_count} tranches
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-1.5 text-right mono">{formatINR(r.gross_amount)}</td>
                  <td className="px-4 py-1.5 text-right mono text-text-muted">{formatINR(r.tds_amount)}</td>
                  <td className="px-4 py-1.5 text-right mono">
                    {formatINR(r.net_amount)}
                    {/* net < gross - TDS means a one-off deduction was taken out
                        of this payout. Without saying so the row looks like an
                        underpayment — which is exactly how three July payouts
                        read until someone dug into the adjustment table. */}
                    {Number(r.gross_amount) - Number(r.tds_amount) - Number(r.net_amount) > 0.005 && (
                      <span className="ml-1 text-xs text-warn" title={`Less a one-off adjustment of ${formatINR(Number(r.gross_amount) - Number(r.tds_amount) - Number(r.net_amount))} — see Adjustments below`}>
                        −{formatINR(Number(r.gross_amount) - Number(r.tds_amount) - Number(r.net_amount))}
                      </span>
                    )}
                  </td>
                  <td className={`px-4 py-1.5 text-xs ${rowPill[r.status] ?? ''}`}>
                    {r.status}
                    {/* A combined row stands for several tranche rows — failing
                        just one from here would be ambiguous, so mark-failed is
                        offered only on single-tranche rows (use the Payouts screen). */}
                    {can('payouts:mark-paid-manual') && r.status === 'Scheduled' && !r.combined && (
                      <button className="ml-2 text-danger hover:underline" title="Mark this row failed (e.g. NEFT bounce)"
                        onClick={async () => {
                          const reason = await promptText({
                            title: 'Mark this payout row failed?',
                            body: 'Use this when the NEFT bounced. The row can be paid again later.',
                            label: 'Reason', confirmLabel: 'Mark failed', danger: true,
                          });
                          if (reason) run(api.post(`/api/payouts/rows/${r.id}/mark-failed`, { reason }));
                        }}>mark failed</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* One-off deductions/additions on this investment's payouts (owner
          2026-08-20: "in the customers investment application i should be able
          to see this detection"). They were recorded and approved all along,
          but only the Payouts page showed them — so the person answering the
          customer could not see why a payout was short. */}
      {(data.adjustments?.length ?? 0) > 0 && (
        <div className="bg-surface border border-border rounded-lg shadow-card mt-4 overflow-hidden">
          <div className="px-4 py-3 border-b border-border text-xs font-semibold text-text-label uppercase tracking-wide">
            Adjustments on payouts
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-text-label border-b border-border">
              <tr>
                <th className="px-4 py-2">Raised</th><th className="px-4 py-2">Type</th>
                <th className="px-4 py-2 text-right">Amount</th>
                <th className="px-4 py-2">Reason</th><th className="px-4 py-2">By</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.adjustments.map((x: any) => (
                <tr key={x.id}>
                  <td className="px-4 py-1.5 mono">{String(x.created_at).slice(0, 10)}</td>
                  <td className={`px-4 py-1.5 ${x.kind === 'Deduction' ? 'text-danger' : 'text-success'}`}>{x.kind}</td>
                  <td className="px-4 py-1.5 text-right mono">
                    {x.kind === 'Deduction' ? '−' : '+'}{formatINR(x.amount)}
                  </td>
                  <td className="px-4 py-1.5 text-text-muted">{x.narration || '—'}</td>
                  <td className="px-4 py-1.5 text-text-muted">{x.created_by ?? '—'}</td>
                  <td className="px-4 py-1.5 text-xs">
                    {x.status}
                    {x.status === 'Consumed' && x.batch_id && (
                      <span className="text-text-muted"> · batch {x.batch_id}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * One credit's money-received date, inside the payment breakup.
 *
 * A clubbed investment is ONE debenture paid for on several days, and each
 * credit earns from its OWN date — so a mistyped date here is real interest.
 * The investment-date editor above cannot help: it refuses a clubbed
 * investment, because there is no single date to move. Before this, fixing one
 * took a hand-written database repair.
 *
 * An NCD Manager+ can REQUEST a correction; it goes to Admin/CXO and the date
 * changes only on approval — the same rule as the investment date above (owner
 * 2026-08-27), because both rebuild the schedule and shift a first period. The
 * API refuses once any interest is paid or batched; that check lives
 * server-side rather than being duplicated here, so there is one source of
 * truth and the reason comes back as a readable message.
 */
function CreditDate({ line, appId, canEdit, onErr }: {
  line: any; appId: number; canEdit: boolean; onErr: (m: string) => void;
}) {
  const cur = line.date_money_received ? String(line.date_money_received).slice(0, 10) : null;
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(cur ?? '');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/api/applications/${appId}/lines/${line.id}/date`, { date: val }); setEditing(false); setSent(true); onErr(''); }
    catch (e) { onErr(e instanceof ApiError ? e.message : 'Failed'); }
    finally { setBusy(false); }
  };
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input type="date" className="text-xs border border-border-strong rounded px-1.5 py-0.5" value={val} onChange={(e) => setVal(e.target.value)} />
        <button className="text-xs bg-primary hover:bg-primary-hover text-white rounded px-2 py-0.5 disabled:opacity-40"
          disabled={busy || !val || val === cur} onClick={save}>Request change</button>
        <button className="text-xs text-text-muted hover:underline" onClick={() => setEditing(false)}>cancel</button>
      </span>
    );
  }
  return (
    <>
      {cur ?? '—'}
      {canEdit && !sent && (
        <button className="ml-2 text-xs text-primary hover:underline"
          title="Correct the day this credit's money arrived — it is the day this credit starts earning. Goes to Admin/CXO for approval."
          onClick={() => { setVal(cur ?? ''); setEditing(true); }}>edit</button>
      )}
      {sent && <span className="ml-2 text-xs text-success">Sent for approval</span>}
    </>
  );
}

/** The investment (money-received) date. Always shown under the header; an NCD
 * Manager+ can REQUEST a correction on a single-credit investment while no interest
 * has been paid — it goes to Admin/CXO approval, and only on approval does the API
 * rebuild the schedule. Refused once anything is paid/batched. */
function InvestmentDate({ a, appId, canEdit, onErr }: {
  a: any; appId: number; canEdit: boolean; onErr: (m: string) => void;
}) {
  const cur = a.date_money_received ? String(a.date_money_received).slice(0, 10) : null;
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(cur ?? '');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/api/applications/${appId}/investment-date`, { date: val }); setEditing(false); setSent(true); }
    catch (e) { onErr(e instanceof ApiError ? e.message : 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <p className="text-sm text-text-muted mt-0.5">
      Investment date: <span className="text-text font-medium mono">{cur ?? '—'}</span>
      {canEdit && !editing && !sent && (
        <button className="ml-2 text-xs text-primary hover:underline" onClick={() => { setVal(cur ?? ''); setEditing(true); }}>edit</button>
      )}
      {sent && <span className="ml-2 text-xs text-success">Sent for Admin/CXO approval — the date changes once approved.</span>}
      {editing && (
        <span className="ml-2 inline-flex items-center gap-1">
          <input type="date" className="text-xs border border-border-strong rounded px-1.5 py-0.5" value={val} onChange={(e) => setVal(e.target.value)} />
          <button className="text-xs bg-primary hover:bg-primary-hover text-white rounded px-2 py-0.5 disabled:opacity-40"
            disabled={busy || !val || val === cur} onClick={save}>Request change</button>
          <button className="text-xs text-text-muted hover:underline" onClick={() => setEditing(false)}>cancel</button>
        </span>
      )}
    </p>
  );
}

/**
 * "Bond given to customer" — WRITE-ONCE (owner 2026-08-28).
 *
 * It used to be a plain checkbox: it stamped the moment you clicked it, said
 * nothing about how the bond travelled, and anyone could quietly untick it and
 * erase the record of who vouched for the handover.
 *
 * Now: marking it asks WHEN it actually reached the customer and HOW ("sent by
 * courier, AWB 123456"), and applies at once. After that the tick is locked —
 * there is no untick. Correcting the date or note, or reversing the whole thing,
 * goes to Admin/CXO approval, which is the only way back.
 *
 * NCD Manager and above only; the server enforces the same rule.
 */
function BondHandover({ app, appId, onDone, onErr }: {
  app: any; appId: number; onDone: () => void; onErr: (m: string) => void;
}) {
  const { user } = useAuth();
  const mayMark = ['ncd_manager', 'admin', 'super_admin'].includes(String(user?.role ?? ''));
  const given = !!app.bond_distributed_at;
  const today = new Date().toISOString().slice(0, 10);

  const [panel, setPanel] = useState<null | 'mark' | 'change'>(null);
  const [date, setDate] = useState(today);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [reverse, setReverse] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const run = async (fn: Promise<unknown>, after: () => void) => {
    setBusy(true);
    try { await fn; after(); onDone(); }
    catch (e) { onErr(e instanceof ApiError ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const onDate = app.bond_distributed_on ? String(app.bond_distributed_on).slice(0, 10) : null;

  return (
    <div className="flex flex-col gap-1">
      <div className={`text-xs flex items-center gap-1.5 border rounded px-3 py-1.5 ${given ? 'border-[color:var(--success)] text-success' : 'border-border'}`}
        title={given
          ? `Recorded by ${app.bond_distributed_by_name ?? 'someone'}${onDate ? ` · given ${onDate}` : ''}`
          : 'Record that the bond certificate reached the customer'}>
        {/* Deliberately NOT an <input> once given: a checkbox invites an untick
            that is no longer allowed, and a disabled one still looks clickable. */}
        <span aria-hidden>{given ? '☑' : '☐'}</span>
        Bond given to customer
        {given && (
          <span className="text-text-muted">
            · {onDate ?? String(app.bond_distributed_at).slice(0, 10)}
            {app.bond_distributed_by_name ? ` · ${app.bond_distributed_by_name}` : ''}
          </span>
        )}
        {!given && mayMark && !panel && (
          <button className="ml-1 text-primary hover:underline" onClick={() => { setDate(today); setNote(''); setPanel('mark'); }}>
            mark as given
          </button>
        )}
        {given && mayMark && !panel && !sent && (
          <button className="ml-1 text-text-muted hover:underline" onClick={() => { setDate(onDate ?? today); setNote(app.bond_distributed_note ?? ''); setReason(''); setReverse(false); setPanel('change'); }}>
            request change
          </button>
        )}
      </div>

      {given && app.bond_distributed_note && (
        <div className="text-[11px] text-text-muted pl-1">{app.bond_distributed_note}</div>
      )}
      {sent && <div className="text-[11px] text-success pl-1">Sent for Admin/CXO approval — it changes once approved.</div>}

      {panel === 'mark' && (
        <div className="border border-border rounded p-2 flex flex-col gap-1.5 max-w-[420px]">
          <label className="text-[11px] text-text-label">Date given to the customer
            <input type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)}
              className="ml-1.5 text-xs border border-border-strong rounded px-1.5 py-0.5" />
          </label>
          <label className="text-[11px] text-text-label">Notes
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
              placeholder="Sent by courier, AWB 123456 / handed to the son"
              className="ml-1.5 text-xs border border-border-strong rounded px-1.5 py-0.5 w-full mt-0.5" />
          </label>
          <div className="text-[11px] text-text-muted">Once saved this cannot be unticked — a change needs Admin/CXO approval.</div>
          <div className="flex gap-2">
            <button className="text-xs bg-primary hover:bg-primary-hover text-white rounded px-2 py-0.5 disabled:opacity-40"
              disabled={busy || !date}
              onClick={() => run(api.post(`/api/applications/${appId}/bond-distributed`, { given_on: date, note: note || undefined }),
                () => setPanel(null))}>Save</button>
            <button className="text-xs text-text-muted hover:underline" onClick={() => setPanel(null)}>cancel</button>
          </div>
        </div>
      )}

      {panel === 'change' && (
        <div className="border border-border rounded p-2 flex flex-col gap-1.5 max-w-[420px]">
          <label className="text-[11px] flex items-center gap-1.5">
            <input type="checkbox" checked={reverse} onChange={(e) => setReverse(e.target.checked)} />
            Reverse it — the bond was NOT given
          </label>
          {!reverse && (
            <>
              <label className="text-[11px] text-text-label">Date given
                <input type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)}
                  className="ml-1.5 text-xs border border-border-strong rounded px-1.5 py-0.5" />
              </label>
              <label className="text-[11px] text-text-label">Notes
                <input type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
                  className="ml-1.5 text-xs border border-border-strong rounded px-1.5 py-0.5 w-full mt-0.5" />
              </label>
            </>
          )}
          <label className="text-[11px] text-text-label">Reason (required)
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              className="ml-1.5 text-xs border border-border-strong rounded px-1.5 py-0.5 w-full mt-0.5" />
          </label>
          <div className="flex gap-2">
            <button className="text-xs bg-primary hover:bg-primary-hover text-white rounded px-2 py-0.5 disabled:opacity-40"
              disabled={busy || reason.trim().length < 2 || (!reverse && !date)}
              onClick={() => run(
                api.patch(`/api/applications/${appId}/bond-distributed`,
                  { given_on: reverse ? null : date, note: reverse ? null : (note || null), reason }),
                () => { setPanel(null); setSent(true); })}>Request change</button>
            <button className="text-xs text-text-muted hover:underline" onClick={() => setPanel(null)}>cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** "Credited to" — which Dhanam account this investment's money landed in. Shown
 * under the header; editable (a select of collection accounts) for staff who can
 * update applications. Wealth had this; NCD never captured it until now. */
function CreditedTo({ app, appId, canEdit, onDone, onErr }: {
  app: any; appId: number; canEdit: boolean; onDone: () => void; onErr: (m: string) => void;
}) {
  const banks = useQuery({
    queryKey: ['collection-banks'],
    // /api/banks — productsRouter is mounted at `/api`, so /api/products/banks
    // is a 404 and this select silently rendered empty.
    queryFn: () => api.get<{ rows: any[] }>('/api/banks'),
    enabled: canEdit,
  });
  const set = (bankId: number | null) =>
    api.post(`/api/applications/${appId}/collection-bank`, { bank_id: bankId })
      .then(() => { onErr(''); onDone(); })
      .catch((e) => onErr(e instanceof ApiError ? e.message : 'Failed'));
  const label = app.collection_bank_id
    ? (app.collection_bank_label || `${app.collection_bank_name ?? ''} ${app.collection_bank_account ?? ''}`.trim() || 'Set')
    : null;
  // Only collection accounts; keep inactive ones so a historical pick stays selectable.
  const opts = (banks.data?.rows ?? []).filter((b: any) => b.is_collection_account);
  return (
    <div className="flex items-center gap-2 mt-1 text-sm">
      <span className="text-text-muted">Credited to:</span>
      {canEdit ? (
        <select className="text-xs border border-border-strong rounded px-2 py-1 bg-surface"
          value={app.collection_bank_id ?? ''}
          onChange={(e) => set(e.target.value ? Number(e.target.value) : null)}
          title="Which Dhanam account received this investment's money">
          <option value="">— not set —</option>
          {banks.isError && <option value="" disabled>Could not load company accounts</option>}
          {opts.map((b: any) => (
            <option key={b.id} value={b.id}>
              {(b.account_label || b.bank_name)} · {b.account_number}{b.is_active ? '' : ' (inactive)'}
            </option>
          ))}
        </select>
      ) : (
        <span className={label ? 'font-medium' : 'text-text-muted'}>{label ?? 'not set'}</span>
      )}
    </div>
  );
}
